/**
 * Routes that the isolated fixture Preview may expose without a customer
 * session. It is deliberately discovery-only: catalog, map and details are
 * useful for device QA, while booking, payment, hosting and trip operations
 * remain protected by real authentication.
 */
const AUTHENTICATED_TOUR_PATHS = new Set([
  'bookings',
  'checkout',
  'host',
  'payment-complete',
  'review',
  'trip',
])

const AUTHENTICATED_PREVIEW_PATHS = new Set([
  'bookings',
  'checkout',
  'host',
  'review',
  'split',
  'trip',
])

export function isAnonymousPreviewRoute(pathname: string): boolean {
  // Expo Router usually strips route groups from usePathname(), but normalize
  // them too so native deep links and tests have one unambiguous contract.
  const normalized = pathname.replace(/^\/\(tabs\)(?=\/|$)/, '') || '/'

  // `/preview` is a fixture-only alias for Tours discovery. Do not make the
  // entire alias public: its checkout, operations and payment helpers are
  // intentionally separate routes and must still require a fixture session.
  if (normalized === '/preview') return true
  if (normalized.startsWith('/preview/')) {
    const firstPreviewSegment = normalized.slice('/preview/'.length).split('/')[0] ?? ''
    if (firstPreviewSegment === 'guardian') return true
    return !AUTHENTICATED_PREVIEW_PATHS.has(firstPreviewSegment)
  }

  if (normalized === '/explore' || normalized.startsWith('/explore/')) return true
  // Services is a discovery surface in the isolated Preview. Creating a
  // request and viewing its operational state stay protected below this
  // root, so QA can inspect the new tab without accidentally exercising a
  // production-like mutation flow while signed out.
  if (normalized === '/services') return true
  if (normalized === '/tours') return true
  if (!normalized.startsWith('/tours/')) return false

  const firstTourSegment = normalized.slice('/tours/'.length).split('/')[0] ?? ''
  return !AUTHENTICATED_TOUR_PATHS.has(firstTourSegment)
}

/**
 * The fixture Preview deliberately has two kinds of unsigned-in surfaces:
 * discovery (which stays anonymous) and the normal auth stack (where a QA
 * tester can establish a fixture-only session). Keeping that distinction in
 * one predicate prevents an accidental broad "Preview is public" exception
 * from exposing checkout or operations without authentication.
 */
export function isPreviewUnauthenticatedRoute(pathname: string, isAuthRoute: boolean): boolean {
  return isAuthRoute || isAnonymousPreviewRoute(pathname)
}
